/**
 * Acting-branch selector.
 *
 * Every operational request carries the branch chosen here as
 * `X-Acting-Branch-Id`, so this control decides what the whole shell is
 * looking at. Two consequences shape the component:
 *
 *   - Cached rows belong to the branch they were fetched under. Switching
 *     therefore drops them instead of leaving one branch's data on screen
 *     beneath another branch's name.
 *   - The server's assignment list is final. The options are the intersection
 *     of `allowed_branch_ids` and the branches the operator may read; the
 *     store refuses anything outside it anyway.
 *
 * An operator assigned to one branch still sees it named here — "which branch
 * am I posting to" is worth answering even when there is only one answer.
 */
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Building2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@erp/ui";

import { apiFetch, type Page } from "@/lib/api/http";
import { useCurrentBranch } from "@/lib/branch/branch-store";
import { useSession } from "@/lib/session/use-session";

interface BranchOption {
  id: string;
  code: string;
  name: string;
  name_ar?: string | null;
}

/** Branch names are company-level reference data, so the list is not itself
 *  branch-scoped: asking for it through the acting branch would make the
 *  control depend on the value it exists to change. */
async function fetchBranches(): Promise<BranchOption[]> {
  const page = await apiFetch<Page<BranchOption>>("/branches", {
    query: { limit: 200 },
  });
  return page.items;
}

export function BranchSwitcher() {
  const { t, i18n } = useTranslation();
  const { user, hasPermission } = useSession();
  const { branchId, setBranchId } = useCurrentBranch();
  const queryClient = useQueryClient();

  const allowed = user?.allowed_branch_ids ?? [];
  const readable = hasPermission("platform.branch.read");

  const query = useQuery({
    queryKey: ["branch-switcher", user?.company_id ?? null],
    enabled: allowed.length > 0 && readable,
    staleTime: 5 * 60 * 1000,
    queryFn: fetchBranches,
  });

  const options = (query.data ?? []).filter((branch) => allowed.includes(branch.id));

  // Without names there is nothing to show but a UUID, which is worse than
  // showing nothing: the operator still acts at their resolved branch.
  if (options.length === 0) return null;

  const onChange = (next: string): void => {
    if (next === branchId) return;
    setBranchId(next);
    // The session survives the switch; every other cached response was read
    // under the old branch and must be re-read rather than re-shown.
    queryClient.removeQueries({ predicate: (entry) => entry.queryKey[0] !== "session" });
  };

  const label = (branch: BranchOption): string => {
    const arabic = i18n.language.startsWith("ar") ? branch.name_ar : null;
    return `${branch.code} — ${arabic ?? branch.name}`;
  };

  return (
    <Select value={branchId ?? undefined} onValueChange={onChange}>
      <SelectTrigger className="w-56" aria-label={t("topbar.branch")}>
        <Building2 className="size-4 shrink-0 opacity-60" aria-hidden="true" />
        <SelectValue placeholder={t("topbar.branchPlaceholder")} />
      </SelectTrigger>
      <SelectContent>
        {options.map((branch) => (
          <SelectItem key={branch.id} value={branch.id}>
            {label(branch)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
